/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2021, 2024
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/
import {
  Button,
  TextInput,
  TextInputSkeleton,
  InlineLoading
} from 'carbon-components-react';
import React, { useCallback, useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { shallowEqual } from 'react-redux';

import { useIsNewIA } from '@ibm-watson/shared/modules/ia/hooks/useIsNewIA';
import { useAppDispatch, useAppSelector } from '@ibm-watson/shared/store';
import { ApiStatus, LoaderStates } from '@ibm-watson/shared/ts';
import { FormattedMessageWithLink } from '../../../helpers/linkHelpers';
import { useFormHelpers } from '../../../hooks/useFormHelpers';
import { disableSaveCreateButton } from '../../../store/channels/actions';
import { connectServiceDeskIntegration } from '../../../store/service-desks/actions';
import {
  getConnectServiceDeskStatus,
  isLoadServiceDeskLoading,
  selectDraftServiceDesk,
  selectShouldSaveServiceDesk
} from '../../../store/service-desks/selectors';
import { links } from '../links';
import {
  ServiceDesk as commonMessages,
  Zendesk as messages
} from '../messages';
import { FIELDS } from './schema';

const IDS = {
  ACCOUNT_KEY: 'zendesk_account_key',
  CONNECT_ACCOUNT_BUTTON: 'zendesk_connect_account'
};

interface ZendeskConnectProps {
  /**
   *
   */
  assistantId: string;
  /**
   *
   */
  environmentId: string | undefined;
  /**
   *
   */
  formMethods: any;
  /**
   *
   */
  markStepComplete?: (complete: boolean) => void;
  /**
   *
   */
  readOnly?: boolean;
  /**
   *
   */
  updateServiceDeskId: (id: string) => void;
}
export const Connect = ({
  assistantId,
  environmentId,
  formMethods,
  markStepComplete,
  updateServiceDeskId,
  readOnly
}: ZendeskConnectProps) => {
  const dispatch = useAppDispatch();
  const { formatMessage: t } = useIntl();

  const { isValidAndPopulated, registerInputField } =
    useFormHelpers(formMethods);

  const connectServiceDeskStatus = useAppSelector(getConnectServiceDeskStatus);
  const draftServiceDesk = useAppSelector(selectDraftServiceDesk, shallowEqual);
  const isServiceDeskLoading = useAppSelector(isLoadServiceDeskLoading);
  const shouldSaveServiceDesk = useAppSelector(selectShouldSaveServiceDesk);

  const accountKeyState = useState<boolean>(false);
  const isAccountKeyValidAndPopulated = isValidAndPopulated(
    FIELDS.ACCOUNT_CONNECTION_KEY
  );

  const isConnectButtonDisabled =
    readOnly || // no write access
    !isAccountKeyValidAndPopulated || // account key hasn't changed
    !shouldSaveServiceDesk; // nothing new to connect or save

  const [statusMessage, setStatusMessage] = useState<string>('');

  /**
   * Connect the Zendesk integration and call update the service
   * desk ID inside the webchat's settings.
   */
  const onConnectAccount = useCallback(async () => {
    const connectedIntegration = await dispatch(
      connectServiceDeskIntegration({
        assistantId,
        environmentId,
        integration: draftServiceDesk
      })
    ).unwrap();

    updateServiceDeskId(connectedIntegration?.integration_id);
  }, [
    assistantId,
    dispatch,
    draftServiceDesk,
    environmentId,
    updateServiceDeskId
  ]);

  /**
   * If the connect account button is enabled, then the user cannot
   * click on Save and Exit before connecting their Zendesk account.
   */
  useEffect(() => {
    dispatch(
      disableSaveCreateButton(
        !isConnectButtonDisabled || !isAccountKeyValidAndPopulated
      )
    );
  }, [dispatch, isAccountKeyValidAndPopulated, isConnectButtonDisabled]);

  /**
   * Determine when the Next button should be enabled using whether
   * or not the button and deployment codes are populated and dirty.
   */
  useEffect(() => {
    const [accountKeyDirty] = accountKeyState;
    const isNextButtonEnabled =
      (connectServiceDeskStatus === ApiStatus.SUCCEEDED || // connect succeeded
        (!shouldSaveServiceDesk && !accountKeyDirty)) &&
      isAccountKeyValidAndPopulated; // no changes required to save

    markStepComplete(isNextButtonEnabled);
  }, [
    connectServiceDeskStatus,
    isAccountKeyValidAndPopulated,
    markStepComplete,
    shouldSaveServiceDesk,
    accountKeyState
  ]);

  /**
   * Show an error message depending on what the connect account
   * status is.
   */
  useEffect(() => {
    let message = null;
    switch (connectServiceDeskStatus) {
      case ApiStatus.LOADING:
        message = t(commonMessages.connecting);
        break;
      case ApiStatus.ERROR:
        message = t(commonMessages.notConnected);
        break;
      case ApiStatus.SUCCEEDED:
        message = t(commonMessages.connected);
        break;
      default:
        message = '';
    }
    setStatusMessage(message);
  }, [t, connectServiceDeskStatus, setStatusMessage]);

  return (
    <>
      <div className="TabContent__field-container">
        <h5 className="TabContent__inner-title TabContent__inner-title-heading">
          <FormattedMessage {...messages.recommendedAccessTitle} />
        </h5>
        <p>
          <FormattedMessageWithLink
            url={
              useIsNewIA()
                ? links.learnMoreDeployZendeskNewIA
                : links.learnMoreDeployZendeskClassic
            }
            {...messages.recommendedAccessDescription}
          />
        </p>
      </div>
      <div className="TabContent__field-container">
        <h5 className="TabContent__inner-title TabContent__inner-title-heading">
          <FormattedMessage {...messages.connectAccountTitle} />
        </h5>
        <p>
          <FormattedMessageWithLink
            url={
              useIsNewIA()
                ? links.learnMoreZendeskAccountKeyNewIA
                : links.learnMoreZendeskAccountKeyClassic
            }
            {...messages.connectAccountDescription}
          />
        </p>
        <div className="TabContent__spacer16"></div>
        {isServiceDeskLoading ? (
          <TextInputSkeleton
            id={IDS.ACCOUNT_KEY}
            className="TabContent__field"
            hideLabel={false}
          />
        ) : (
          <TextInput
            {...registerInputField({
              field: FIELDS.ACCOUNT_CONNECTION_KEY,
              labelText: messages.accountKeyLabel,
              placeholderText: messages.accountKeyPlaceholder,
              changeState: accountKeyState,
              readOnly
            })}
            id={IDS.ACCOUNT_KEY}
            className="TabContent__field"
          />
        )}
      </div>
      <div className="ServiceDesk__connect-button-container">
        <Button
          kind="tertiary"
          onClick={onConnectAccount}
          disabled={isConnectButtonDisabled}
          size="sm"
          id={IDS.CONNECT_ACCOUNT_BUTTON}>
          <FormattedMessage {...commonMessages.connectAccountButton} />
        </Button>
        {connectServiceDeskStatus !== ApiStatus.IDLE && (
          <div className="ServiceDesk__connect-button-status">
            <InlineLoading
              description={statusMessage}
              status={
                connectServiceDeskStatus === ApiStatus.SUCCEEDED
                  ? LoaderStates.SUCCESS
                  : connectServiceDeskStatus === ApiStatus.ERROR
                  ? LoaderStates.ERROR
                  : LoaderStates.ACTIVE
              }
            />
          </div>
        )}
      </div>
    </>
  );
};
